import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './FlashcardsPage.css';
import DemoFlashcard from './DemoFlashcard';
import { getWordKey } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';

// Demo SRS system - separate from real SRS
const getDemoSRSData = (key) => {
  const data = localStorage.getItem(`demo_${key}`);
  if (!data) return {
    learning_interval: 0,
    ease: 2.5,
    nextDue: 0,
    history: [],
    apparentTier: 1,
    lastUpdated: Date.now()
  };
  try {
    const parsed = JSON.parse(data);
    return {
      learning_interval: 0,
      ease: 2.5,
      nextDue: 0,
      history: [],
      apparentTier: 1,
      lastUpdated: Date.now(),
      ...parsed
    };
  } catch {
    return {
      learning_interval: 0,
      ease: 2.5,
      nextDue: 0,
      history: [],
      apparentTier: 1,
      lastUpdated: Date.now()
    };
  }
};

const updateDemoSRSData = (key, knewIt) => {
  const now = Date.now();
  let srs = getDemoSRSData(key);
  const history = [...srs.history, { result: knewIt ? 1 : 0, time: now }].slice(-20);
  let interval = srs.learning_interval;
  let ease = srs.ease;

  if (knewIt) {
    interval = interval === 0 ? 1 : Math.round(interval * ease);
    ease = Math.min(ease + 0.1, 3.0);
  } else {
    interval = 1;
    ease = Math.max(1.3, ease - 0.2);
  }

  const nextDue = now + interval * 86400000; // 24 hours in milliseconds

  const trueTier = interval >= 10 ? 5 : interval >= 4 ? 4 : interval >= 2 ? 3 : interval >= 1 ? 2 : 1;
  let apparentTier = srs.apparentTier;

  if (srs.apparentTier === 1 && srs.history.length === 0) {
    apparentTier = knewIt ? 3 : 2;
  } else if (!knewIt) {
    apparentTier = Math.max(2, srs.apparentTier - 1);
  } else {
    if (trueTier > srs.apparentTier) {
      apparentTier = srs.apparentTier + 1;
      if (apparentTier < trueTier) apparentTier = trueTier;
      if (apparentTier > trueTier + 1) apparentTier = trueTier + 1;
    }
  }

  const updated = {
    learning_interval: interval,
    ease,
    history,
    nextDue,
    apparentTier,
    lastUpdated: now
  };

  localStorage.setItem(`demo_${key}`, JSON.stringify(updated));
  return updated;
};

const getDemoSRSStatus = (srs, t) => {
  const tier = srs?.apparentTier ?? 1;
  const map = {
    1: { label: t('newWord'), color: '#aaa' },
    2: { label: t('needsWork'), color: '#e74c3c' },
    3: { label: t('gettingThere'), color: '#f39c12' },
    4: { label: t('nearlyMastered'), color: '#27ae60' },
    5: { label: t('mastered'), color: '#2ecc71' }
  };
  return map[tier] || map[1];
};

const DEMO_MODULE = {
  id: 'demo',
  from_lang: 'English',
  to_lang: 'Spanish',
  from: 'English',
  to: 'Spanish',
  topic: 'Travel',
  dialect: 'Mexican',
  word_groups: [
    {
      name: 'Travel Essentials',
      words: [
        { English: 'to travel', Spanish: 'viajar' },
        { English: 'airport', Spanish: 'el aeropuerto' },
        { English: 'airplane ticket', Spanish: 'el boleto de avión' },
        { English: 'passport', Spanish: 'el pasaporte' },
        { English: 'hotel', Spanish: 'el hotel' },
        { English: 'reservation', Spanish: 'la reservación' },
        { English: 'to check in', Spanish: 'registrarse' },
        { English: 'to check out', Spanish: 'retirarse' },
        { English: 'bus', Spanish: 'el camión' },
        { English: 'taxi', Spanish: 'el taxi' },
        { English: 'subway, metro', Spanish: 'el metro' },
        { English: 'where is...?', Spanish: '¿Dónde está...?' },
        { English: 'how much does it cost?', Spanish: '¿Cuánto cuesta?' },
        { English: 'the bill, the check', Spanish: 'la cuenta' },
        { English: 'please', Spanish: 'por favor' },
        { English: 'thank you', Spanish: 'gracias' },
        { English: 'I am lost', Spanish: 'Estoy perdido/a' },
        { English: 'help', Spanish: '¡Auxilio!, ¡Ayuda!' },
        { English: 'I need a doctor', Spanish: 'Necesito un doctor' },
        { English: 'What\'s up?', Spanish: '¿Qué onda?' },
        { English: 'cool, awesome', Spanish: 'chido' },
        { English: 'No way!, You\'re kidding!', Spanish: '¡No manches!' },
        { English: 'Watch out!, Be careful!', Spanish: '¡Aguas!' },
        { English: 'right on!, go for it!, wow!', Spanish: '¡Órale!' },
        { English: 'dude, guy', Spanish: 'güey' },
      ],
    },
  ],
};

function DemoFlashcardsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const mod = DEMO_MODULE;
  const groupIdx = 0;
  // Convert demo words to the format expected by Flashcard component
  const words = mod.word_groups[0].words.map(word => ({
    from_text: word[mod.from],
    to_text: word[mod.to],
    id: `demo_${word[mod.from]}_${word[mod.to]}`
  }));

  // Classic mode state
  const [classicCardIndex, setClassicCardIndex] = useState(0);
  const [classicIsFlipped, setClassicIsFlipped] = useState(false);
  const [classicActiveWords, setClassicActiveWords] = useState(words);

  // Learning mode state
  const [learningCardIndex, setLearningCardIndex] = useState(() => Math.floor(Math.random() * words.length));
  const [learningIsFlipped, setLearningIsFlipped] = useState(false);
  const [learningActiveWords] = useState(words);

  // Add local SRS state for all words
  const [srsMap, setSrsMap] = useState({});
  // Handler to update SRS state for a word
  const handleSRSUpdate = useCallback((key, data) => {
    setSrsMap(prev => ({ ...prev, [key]: data }));
  }, []);


  // Classic mode handlers
  const handleNextCard = useCallback(() => {
    setClassicCardIndex((prevIndex) => (prevIndex + 1) % classicActiveWords.length);
    setClassicIsFlipped(false);
  }, [classicActiveWords.length]);
  const handlePrevCard = useCallback(() => {
    setClassicCardIndex((prevIndex) => (prevIndex - 1 + classicActiveWords.length) % classicActiveWords.length);
    setClassicIsFlipped(false);
  }, [classicActiveWords.length]);
  const handleFlipCard = useCallback(() => {
    setClassicIsFlipped((prev) => !prev);
  }, []);
  const handleShuffle = useCallback(() => {
    setClassicActiveWords((prevWords) => {
      const shuffled = [...prevWords];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    });
    setClassicCardIndex(0);
    setClassicIsFlipped(false);
  }, []);

  // Learning mode handlers
  const getOptimalWeight = useCallback((word) => {
    const srs = getDemoSRSData(getWordKey(word, mod.id, groupIdx));
    const now = Date.now();
    const isDue = srs.nextDue <= now;
    const recentMistakes = (srs.history || []).slice(-3).filter(h => !h.result).length;
    const interval = srs.learning_interval;
    let weight = 1;
    if (isDue) weight *= 4;
    if (recentMistakes > 0) weight *= 2;
    if (interval < 2) weight *= 1.5;
    if (interval > 10) weight *= 0.3;
    return weight;
  }, [mod.id, groupIdx]);

  function weightedRandomIndex(weights) {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
      if (r < weights[i]) return i;
      r -= weights[i];
    }
    return weights.length - 1;
  }
  const handleLearningNext = useCallback(() => {
    const weights = learningActiveWords.map(getOptimalWeight);
    let nextIdx = weightedRandomIndex(weights);
    if (learningActiveWords.length > 1 && nextIdx === learningCardIndex) {
      nextIdx = weightedRandomIndex(weights);
    }
    setLearningCardIndex(nextIdx);
    setLearningIsFlipped(false);
  }, [learningActiveWords, learningCardIndex, getOptimalWeight]);

  // Fix: Keyboard navigation for classic mode (unconditional hook)
  useEffect(() => {
    if (!location.pathname.endsWith('/flashcards')) return;
    const handleClassicKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextCard();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevCard();
      } else if (e.code === 'Space') {
        // Only handle Space globally if it's not on a flashcard element
        const flashcardElement = e.target.closest && e.target.closest('.flashcard');
        if (!flashcardElement) {
          e.preventDefault();
          handleFlipCard();
        }
      }
    };
    window.addEventListener('keydown', handleClassicKeyDown);
    return () => window.removeEventListener('keydown', handleClassicKeyDown);
  }, [location.pathname, handleNextCard, handlePrevCard, handleFlipCard]);

  // Mode selection page
  if (location.pathname.endsWith('/flashcard-mode')) {
    return (
      <div className="flashcard-mode-container">
        <div className="flashcard-mode-content">
          <button
            onClick={() => navigate('/module/demo/group/0')}
            className="btn btn-secondary btn-back"
          >
            {isRTL ? '→' : '←'} {t('backToModule')}
          </button>
          <div className="mode-selection-card">
            <h1>{t('chooseFlashcardMode')}</h1>
            <p className="mode-description">
              {t('selectHowToPractice')}
            </p>
            <div className="mode-options">
              <button
                className="mode-option learning-mode"
                onClick={() => navigate('/module/demo/group/0/flashcards-learning')}
                aria-label={t('startLearningMode')}
              >
                <div className="mode-icon">🎯</div>
                <h3>{t('learningMode')}</h3>
                <p className="mode-details">
                  {t('learningModeDescription')}
                </p>
                <ul className="mode-features">
                  <li>{t('learningModeFeature1')}</li>
                  <li>{t('learningModeFeature2')}</li>
                  <li>{t('learningModeFeature3')}</li>
                  <li>{t('learningModeFeature4')}</li>
                </ul>
                <span className="btn btn-primary mode-btn">{t('startLearningMode')}</span>
              </button>
              <button
                className="mode-option classic-mode"
                onClick={() => navigate('/module/demo/group/0/flashcards')}
                aria-label={t('startClassicMode')}
              >
                <div className="mode-icon">📚</div>
                <h3>{t('classicMode')}</h3>
                <p className="mode-details">
                  {t('classicModeDescription')}
                </p>
                <ul className="mode-features">
                  <li>{t('classicModeFeature1')}</li>
                  <li>{t('classicModeFeature2')}</li>
                  <li>{t('classicModeFeature3')}</li>
                  <li>{t('classicModeFeature4')}</li>
                </ul>
                <span className="btn btn-secondary mode-btn">{t('startClassicMode')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Classic mode (default)
  if (location.pathname.endsWith('/flashcards')) {
    const currentWord = classicActiveWords[classicCardIndex];
    return (
      <div className="flashcards-page-container" style={{ marginTop: '3.5rem' }}>
        <button onClick={() => navigate('/module/demo/group/0')} className="btn btn-secondary btn-back">
          {isRTL ? '→' : '←'} {t('exitFlashcards')}
        </button>
        <div className="flashcard-view-container">
          <DemoFlashcard
            key={`${classicCardIndex}-${getWordKey(currentWord, mod.id, groupIdx)}`}
            word={currentWord}
            module={mod}
            isFlipped={classicIsFlipped}
            onFlip={handleFlipCard}
            onFeedback={handleNextCard}
            onSRSUpdate={handleSRSUpdate}
            srsMap={srsMap}
            setSrsMap={setSrsMap}
            getDemoSRSData={getDemoSRSData}
            updateDemoSRSData={updateDemoSRSData}
            getDemoSRSStatus={getDemoSRSStatus}
          />
          <div className="flashcard-nav">
            <button onClick={handlePrevCard} className="btn btn-secondary">
              {isRTL ? '→' : '←'} {t('prev')}
            </button>
            <span>{isRTL ? `${classicActiveWords.length} / ${classicCardIndex + 1}` : `${classicCardIndex + 1} / ${classicActiveWords.length}`}</span>
            <button onClick={handleNextCard} className="btn btn-secondary">
              {t('next')} {isRTL ? '←' : '→'}
            </button>
          </div>
          <button onClick={handleShuffle} className="btn btn-secondary btn-shuffle">{t('shuffleCards')}</button>
          <div className="flashcard-hotkeys" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            {t('flashcardHotkeys').replace('{arrows}', isRTL ? '→/←' : '←/→')}
          </div>
        </div>
      </div>
    );
  }

  // Learning mode
  if (location.pathname.endsWith('/flashcards-learning')) {
    const currentWord = learningActiveWords[learningCardIndex];
    return (
      <div className="flashcards-page-container">
        <button onClick={() => navigate('/module/demo/group/0')} className="btn btn-secondary btn-back">
          {isRTL ? '→' : '←'} {t('exitFlashcards')}
        </button>
        <div className="flashcard-view-container">
          <DemoFlashcard
            key={`${learningCardIndex}-${getWordKey(currentWord, mod.id, groupIdx)}`}
            word={currentWord}
            module={mod}
            isFlipped={learningIsFlipped}
            onFlip={() => setLearningIsFlipped(f => !f)}
            onFeedback={handleLearningNext}
            onSRSUpdate={handleSRSUpdate}
            srsMap={srsMap}
            setSrsMap={setSrsMap}
            getDemoSRSData={getDemoSRSData}
            updateDemoSRSData={updateDemoSRSData}
            getDemoSRSStatus={getDemoSRSStatus}
          />
          <div className="learning-mode-info" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            {t('learningModeInfo')}
          </div>
        </div>
      </div>
    );
  }

  // fallback
  return null;
}

export default DemoFlashcardsPage; 